import type { DataColumn } from "../types";
import { columnDataTypeCategory } from "./operatorCatalog";

// --- Temporal precision -------------------------------------------------------------
// columnDataTypeCategory collapses every temporal SQL type into one category, "date". That
// is the right granularity for choosing OPERATORS, and the wrong one for choosing how two
// temporal columns are COMPARED: a `date` column and a `datetime2` column are both "date",
// and comparing them directly makes SQL Server widen the `date` to MIDNIGHT. The condition
// is then true only at exactly 00:00:00.000, which reads as "always false" against real
// data - a plausible-looking empty result rather than an error.
//
// This module is the finer classification that fixes that. Three rules follow from it:
//
//   1. Compare at the COARSER of the two precisions. Precision can be discarded truthfully;
//      it can never be added. Truncating the finer side is the only direction that does not
//      fabricate a value the data never held.
//   2. Decide by TYPE, never by value. Checking whether a datetime2 happens to be midnight
//      would make the rule behave differently row by row - the opposite of predictable - and
//      would silently reclassify a genuine midnight departure.
//   3. Only truncate when the two tiers DIFFER (or the user asks explicitly). A pair of
//      columns at the same tier emits exactly what it emitted before this module existed,
//      which is what confines the change to the pairings that are broken.

/** How precise a temporal column is, coarsest first. */
export type TemporalTier = "day" | "minute" | "second";

/**
 * Whether a column carries a date part or is a bare time of day. Two temporal columns are
 * only comparable when they share a shape: `time` against a timestamp is not an edge case
 * to handle gracefully, it is a pairing with no meaning - SQL Server widens `time` using
 * 1900-01-01, so a difference lands in the tens of millions of minutes.
 */
export type TemporalShape = "date" | "timeOfDay";

/** The order that makes "coarser" meaningful. */
const TIER_RANK: Record<TemporalTier, number> = { day: 0, minute: 1, second: 2 };

const DAY_TYPES = ["date"];
const MINUTE_TYPES = ["smalldatetime"];
const TIME_OF_DAY_TYPES = ["time"];
const OFFSET_TYPES = ["datetimeoffset"];

function normalizedType(column?: DataColumn | null) {
    return String(column?.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
}

/** True for any column the builder treats as temporal at all. */
export function isTemporalColumn(column?: DataColumn | null) {
    return columnDataTypeCategory(column) === "date";
}

/**
 * The tier of a temporal column, or null when it is not temporal.
 *
 * A text-backed date column reports "second": its real precision is unknowable from
 * `varchar`, and assuming the finest tier means nothing is silently truncated without the
 * user asking. The explicit "Compare by" control is the escape hatch for those.
 */
export function temporalTier(column?: DataColumn | null): TemporalTier | null {
    if (!isTemporalColumn(column)) return null;
    const type = normalizedType(column);
    if (DAY_TYPES.includes(type)) return "day";
    if (MINUTE_TYPES.includes(type)) return "minute";
    return "second";
}

export function temporalShape(column?: DataColumn | null): TemporalShape | null {
    if (!isTemporalColumn(column)) return null;
    return TIME_OF_DAY_TYPES.includes(normalizedType(column)) ? "timeOfDay" : "date";
}

/**
 * True for a column that carries its own UTC offset.
 *
 * Mixing one with an offset-naive column is NOT handled here, deliberately. SQL Server
 * compares them by treating the naive side as UTC, so if these tables hold local time every
 * such comparison is silently wrong by the offset - shifted but plausible, which is the
 * hardest kind of wrong to notice. Whether that applies depends on how the catalog's
 * datetime2 columns are populated, which is not recorded anywhere in this codebase, so
 * mixed pairs are excluded from the pickers until that is settled rather than being given a
 * default that might make the mismatch worse.
 */
export function isOffsetAware(column?: DataColumn | null) {
    return OFFSET_TYPES.includes(normalizedType(column));
}

/** Whether two temporal columns may be compared at all. */
export function temporalPairIsComparable(left?: DataColumn | null, right?: DataColumn | null) {
    const leftShape = temporalShape(left);
    const rightShape = temporalShape(right);
    if (!leftShape || !rightShape) return false;
    if (leftShape !== rightShape) return false;
    return isOffsetAware(left) === isOffsetAware(right);
}

export function coarserTier(left: TemporalTier, right: TemporalTier): TemporalTier {
    return TIER_RANK[left] <= TIER_RANK[right] ? left : right;
}

/**
 * The tier two columns should actually be compared at, or null for "leave both alone".
 *
 * Null is the parity case and the common one: two columns of the same tier, with no explicit
 * override, emit exactly the SQL they emitted before this module existed.
 */
export function effectiveComparisonTier(
    left?: DataColumn | null,
    right?: DataColumn | null,
    explicit?: TemporalTier | "exact" | null,
): TemporalTier | null {
    if (explicit === "exact") return null;
    if (explicit) return explicit;
    const leftTier = temporalTier(left);
    const rightTier = temporalTier(right);
    if (!leftTier || !rightTier) return null;
    if (leftTier === rightTier) return null;
    return coarserTier(leftTier, rightTier);
}

/**
 * Truncates an expression to a tier.
 *
 * Compatibility notes, which are why these are not the obvious functions:
 *   - DATETRUNC is SQL Server 2022+ and is deliberately not used.
 *   - CONVERT(smalldatetime, x) ROUNDS rather than truncates - 10:00:31 becomes 10:01 -
 *     so it is not a minute truncation however much it looks like one.
 *   - Plain DATEDIFF(SECOND, ...) overflows beyond ~68 years from its anchor, so the finer
 *     tiers use DATEDIFF_BIG (2016+). The day tier, which is the case that actually matters
 *     for date-vs-datetime, uses CAST(x AS date) and works on every supported version.
 */
/**
 * True when this column actually carries precision finer than `tier`, and so has something
 * to discard.
 *
 * A column already at or coarser than the target tier is left alone: `CAST(SvcDate AS date)`
 * on a column that is already `date` is a no-op that only makes the generated SQL harder to
 * read. Text-backed columns report the finest tier precisely so that they DO get truncated
 * here - their real precision is unknowable, so discarding is the safe direction.
 */
export function columnNeedsTruncation(column: DataColumn | null | undefined, tier: TemporalTier) {
    const own = temporalTier(column);
    if (!own) return false;
    return TIER_RANK[own] > TIER_RANK[tier];
}

/** Truncates a column's SQL to a tier, or returns it untouched when it is already coarser. */
export function truncateColumnToTier(sql: string, column: DataColumn | null | undefined, tier: TemporalTier) {
    return columnNeedsTruncation(column, tier) ? truncateSqlToTier(sql, tier) : sql;
}

export function truncateSqlToTier(sql: string, tier: TemporalTier): string {
    if (tier === "day") return `CAST(${sql} AS date)`;
    const unit = tier === "minute" ? "MINUTE" : "SECOND";
    return `DATEADD(${unit}, DATEDIFF_BIG(${unit}, '20000101', ${sql}), CAST('20000101' AS datetime2))`;
}

// --- Difference units ---------------------------------------------------------------

/** The unit a time-difference condition is measured in. Absent means minutes, as before. */
export type TimeDifferenceUnit = "minute" | "hour" | "day";

const UNIT_RANK: Record<TimeDifferenceUnit, number> = { minute: 0, hour: 1, day: 2 };

export const TIME_DIFFERENCE_UNIT_SQL: Record<TimeDifferenceUnit, string> = {
    minute: "MINUTE",
    hour: "HOUR",
    day: "DAY",
};

/**
 * The units a difference between these two columns may honestly be measured in.
 *
 * A minute-level difference is simply not available when either column lacks a time of day:
 * DATEDIFF(MINUTE, date, datetime) treats the date as midnight and returns the time-of-day
 * of one column measured from a midnight the other never contained - a number that looks
 * like data and is not. Offering days instead turns that dead end into a useful condition
 * rather than a blocked one.
 */
export function availableDifferenceUnits(left?: DataColumn | null, right?: DataColumn | null): TimeDifferenceUnit[] {
    const leftTier = temporalTier(left);
    const rightTier = temporalTier(right);
    if (!leftTier || !rightTier) return ["minute", "hour", "day"];
    const coarser = coarserTier(leftTier, rightTier);
    if (coarser === "day") return ["day"];
    return ["minute", "hour", "day"];
}

/** Coerces a requested unit to one the column pair can honestly express. */
export function resolveDifferenceUnit(
    left: DataColumn | null | undefined,
    right: DataColumn | null | undefined,
    requested?: TimeDifferenceUnit | null,
): TimeDifferenceUnit {
    const available = availableDifferenceUnits(left, right);
    const wanted = requested || "minute";
    if (available.includes(wanted)) return wanted;
    // Fall back to the finest unit the pair can express, never to something finer.
    return available.reduce((best, unit) => (UNIT_RANK[unit] < UNIT_RANK[best] ? unit : best), available[0]);
}

/**
 * The tier a difference's operands should be truncated to, or null to leave them alone.
 *
 * Same parity rule as comparisons: two columns of the same tier measured in the default unit
 * emit exactly what they did before. Truncation appears only where the tiers differ, which
 * is where the fabricated-midnight problem lives.
 */
export function differenceTruncationTier(
    left?: DataColumn | null,
    right?: DataColumn | null,
): TemporalTier | null {
    const leftTier = temporalTier(left);
    const rightTier = temporalTier(right);
    if (!leftTier || !rightTier) return null;
    if (leftTier === rightTier) return null;
    return coarserTier(leftTier, rightTier);
}
